import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { gsap } from 'gsap';
import LogoWhite from '../assets/LOGO_WHITE.png';

const BG_01 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_01.jpg?alt=media&token=9e891581-27bf-4bdd-83cf-a39b44af9324";
const BG_02 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_02.jpg?alt=media&token=b4394dc2-756e-41e8-9cfd-53c25137835d";
const BG_03 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_03.jpg?alt=media&token=989909db-898b-4f93-babf-0262e968ddff";
const BG_04 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_04.jpg?alt=media&token=b895a70e-d345-48b9-aa06-a359057f059a";
const BG_05 = "https://firebasestorage.googleapis.com/v0/b/outlne-6a95c.firebasestorage.app/o/ui_images_backgrounds%2FBG_05.jpg?alt=media&token=d91d94d3-064a-4efa-8bcf-f37cd4a04714";

export default function Blog() {
//Page Title
  useEffect(() => {
  // Set page title
  document.title = "Interior Design | Tips, Trends & Inspiration | Outlne"; // Replace with your page title
  

}, []);

  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeGrid, setActiveGrid] = useState(0);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [bgIndex, setBgIndex] = useState(0);
  const [bgPosition, setBgPosition] = useState({ x: 0, y: 0 });
  const [bgScale, setBgScale] = useState(1);
  const [bgOpacity, setBgOpacity] = useState(0);
  const [gridOpacity, setGridOpacity] = useState(1);
  const [pageLoaded, setPageLoaded] = useState(false);

  const lastGridChangeTime = useRef(0);
  const containerRef = useRef(null);
  const bgIntervalRef = useRef(null);
  const bgAnimationRef = useRef(null);
  const threeMountRef = useRef(null);
  const featuresContainerRef = useRef(null);

  const backgrounds = [BG_01, BG_02, BG_03, BG_04, BG_05];

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      const tablet = width >= 768 && width < 1024;
      
      setIsMobile(mobile);
      setIsTablet(tablet);
      setWindowWidth(width);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Page load effect - fade out grid and fade in background
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
      setGridOpacity(0);
      setBgOpacity(0);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Optimized background slideshow
  useEffect(() => {
    const preloadImages = () => {
      backgrounds.forEach(src => {
        const img = new Image();
        img.src = src;
      });
    };
    preloadImages();

    const changeBackground = () => {
      setBgIndex(prev => (prev + 1) % backgrounds.length);
      
      const direction = Math.random() > 0.5 ? 1 : -1;
      const axis = Math.random() > 0.5 ? 'x' : 'y';
      
      setBgPosition({ x: 0, y: 0 });
      setBgScale(1.05);
      
      setTimeout(() => {
        setBgPosition({
          x: axis === 'x' ? direction * 5 : 0,
          y: axis === 'y' ? direction * 5 : 0
        });
      }, 10);
    };

    if (pageLoaded) {
      changeBackground();
      bgIntervalRef.current = setInterval(changeBackground, 8000);
    }

    return () => {
      clearInterval(bgIntervalRef.current);
      if (bgAnimationRef.current) {
        cancelAnimationFrame(bgAnimationRef.current);
      }
    };
  }, [pageLoaded]);

  // Grid animation - only run after page has loaded
  useEffect(() => {
    if (!pageLoaded) return;

    let animationFrame;
    let lastTime = 0;
    const scrollSpeed = 10;

    const animateScroll = (time) => {
      if (!lastTime) lastTime = time;
      const delta = (time - lastTime) / 1000;
      lastTime = time;
      
      const timeSinceChange = time - lastGridChangeTime.current;
      
      if (timeSinceChange > 100) {
        setScrollPosition(prev => ({
          x: (prev.x + scrollSpeed * delta) % 80,
          y: (prev.y + scrollSpeed * delta) % 80
        }));
      }
      
      animationFrame = requestAnimationFrame(animateScroll);
    };

    animationFrame = requestAnimationFrame(animateScroll);

    const interval = setInterval(() => {
      lastGridChangeTime.current = performance.now();
      setScrollPosition({ x: 0, y: 0 });
      setActiveGrid((prev) => (prev + 1) % 3);
    }, 4000);

    return () => {
      cancelAnimationFrame(animationFrame);
      clearInterval(interval);
    };
  }, [pageLoaded]);

  // Grid styles
  const gridStyles = [
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px)`,
      backgroundSize: '40px 40px',
      display: activeGrid === 0 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) skewX(-15deg)`,
      backgroundSize: '50px 40px',
      backgroundPositionX: '20px',
      display: activeGrid === 1 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    },
    {
      transform: `translate(-${scrollPosition.x}px, -${scrollPosition.y}px) perspective(500px) rotateX(15deg)`,
      backgroundSize: '40px 30px',
      backgroundPositionY: '10px',
      display: activeGrid === 2 ? 'block' : 'none',
      opacity: gridOpacity,
      transition: 'opacity 1s ease'
    }
  ];

  return (
    <>
      {/* Fixed Background Layer */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(29, 29, 29, 0.8)',
        zIndex: -3,
      }} />

      {/* Optimized Background Slideshow - Fades in after load */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -2,
        overflow: 'hidden',
        willChange: 'transform',
        opacity: bgOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          right: '-10%',
          bottom: '-10%',
          backgroundImage: `url(${backgrounds[bgIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transform: `scale(${bgScale}) translate(${bgPosition.x}%, ${bgPosition.y}%)`,
          mixBlendMode: 'soft-light',
          transition: 'background-image 1s ease-in-out, transform 8s linear',
          willChange: 'transform, opacity',
          backfaceVisibility: 'hidden',
          transformStyle: 'preserve-3d',
        }} />
      </div>

      {/* Grid Container - Fades out after load */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: isMobile ? '100vw' : '50vw',
        bottom: 0,
        zIndex: -1,
        overflow: 'hidden',
        opacity: gridOpacity,
        transition: 'opacity 1s ease'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: isMobile ? '100vw' : '200vw',
          maskImage: 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, white 5%, white 95%, transparent 100%)',
        }}>
          {gridStyles.map((style, index) => (
            <div key={index} style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(to right, white 1px, transparent 1px),
                linear-gradient(to bottom, white 1px, transparent 1px)
              `,
              ...style
            }} />
          ))}
        </div>
      </div>

      {/* Desktop Header */}
      {!isMobile && (
        <>
          {/* X Icon - Top Left */}
          <div 
            style={{
              position: 'fixed',
              top: '30px',
              left: '30px',
              zIndex: 20,
              cursor: 'pointer',
              fontSize: '2rem',
              color: 'white',
            }}
            onClick={() => navigate('/')}
          >
            ×
          </div>

          {/* Desktop Logo - Top Right */}
          <div style={{
            position: 'fixed',
            top: '40px',
            right: '40px',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textAlign: 'left',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img 
                src={LogoWhite} 
                alt="Outlne Logo"
                style={{
                  width: '40px',
                  height: 'auto',
                }}
              />
              <h1 style={{
                fontSize: '1.8rem',
                fontFamily: 'avenir-lt-pro, sans-serif',
                letterSpacing: '-1.2px',
                margin: 0,
                color: 'white',
                fontWeight: 500,
              }}>
                Outlne
              </h1>
            </div>
            <p style={{
              fontSize: '1rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 8px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '60px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>
        </>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <>
          {/* Mobile Header - Left Side */}
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            zIndex: 15,
            textAlign: 'left',
          }}>
            <h1 style={{
              fontSize: '1.5rem',
              fontFamily: 'avenir-lt-pro, sans-serif',
              letterSpacing: '-1.2px',
              margin: 0,
              color: 'white',
              fontWeight: 500,
            }}>
              Outlne
            </h1>
            <p style={{
              fontSize: '0.9rem',
              margin: '5px 0 0 0',
              color: 'white',
              fontFamily: 'avenir-lt-pro, sans-serif',
              fontWeight: '500'
            }}>
              Interior Design <span style={{
                backgroundColor: '#ffffff',
                color: 'black',
                padding: '2px 6px',
                borderRadius: '4px',
                display: 'inline-block',
                minWidth: '50px',
                textAlign: 'center'
              }}>
                Editor
              </span>
            </p>
          </div>

          {/* Mobile Logo and X - Top Right */}
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '10px',
          }}>
            <img 
              src={LogoWhite} 
              alt="Outlne Logo"
              style={{
                width: '40px',
                height: '40px',
              }}
            />
            <div 
              style={{
                cursor: 'pointer',
                marginRight: '10px',
                fontSize: '1.8rem',
                color: 'white',
                marginTop: '-5px',
              }}
              onClick={() => navigate('/')}
            >
              ×
            </div>
          </div>
        </>
      )}

      {/* Blog Content - Simple centered message */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isMobile ? '90vw' : isTablet ? '500px' : '600px',
        textAlign: 'center',
        zIndex: 5,
        opacity: pageLoaded ? 1 : 0,
        transition: 'opacity 1s ease',
        color: 'white',
        fontFamily: 'avenir-lt-pro, sans-serif'
      }}>
        <h1 style={{
          fontSize: isMobile ? '2rem' : isTablet ? '2.5rem' : '3rem',
          fontWeight: '500',
          marginBottom: '1rem',
          letterSpacing: '-0.5px'
        }}>
          Blog
        </h1>
        <p style={{
          fontSize: isMobile ? '1rem' : isTablet ? '1.1rem' : '1.2rem',
          lineHeight: '1.6',
          color: '#ccc',
          maxWidth: '500px',
          margin: '0 auto'
        }}>
          There are currently no blog articles or design guides at this moment, come back later.
        </p>
      </div>

      {/* CSS Styles */}
      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 0.7; }
            50% { opacity: 0.2; }
          }
          
          body {
            overflow-x: hidden;
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
          }
          
          * {
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
          }
          
          *:focus {
            outline: none;
          }
        `}
      </style>
    </>
  );
}